import React, { FormEvent, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import useSubmitOnboardingSurvey from "../hooks/useSubmitOnboardingSurvey";
import useOnboardingSurveyStatus from "../hooks/useOnboardingSurveyStatus";

/* ─────────────────────────────── constants ───────────────────────────── */
const TOTAL_STEPS = 7;

const EASE_RATINGS = [
  { value: 1, emoji: "😣", label: "Very Difficult" },
  { value: 2, emoji: "😕", label: "Difficult" },
  { value: 3, emoji: "😐", label: "Neutral" },
  { value: 4, emoji: "🙂", label: "Easy" },
  { value: 5, emoji: "😄", label: "Very Easy" },
];

const TIME_RATINGS = [
  { value: 1, label: "Way too long" },
  { value: 2, label: "A bit slow" },
  { value: 3, label: "About right" },
  { value: 4, label: "Pretty fast" },
  { value: 5, label: "Very fast" },
];

const KYC_RATINGS = [
  { value: 1, label: "Very confusing" },
  { value: 2, label: "Somewhat confusing" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Fairly clear" },
  { value: 5, label: "Very clear & secure" },
];

const NEXT_STEPS_RATINGS = [
  { value: 1, label: "No idea what to do" },
  { value: 2, label: "A bit unclear" },
  { value: 3, label: "Somewhat clear" },
  { value: 4, label: "Mostly clear" },
  { value: 5, label: "Very clear" },
];

const FRICTION_OPTIONS = [
  "Email/Phone Verification",
  "Creating a Password",
  "Inputting Personal/Business Details",
  "Uploading Documents",
  "Navigating the Dashboard afterward",
  "None, it was seamless",
];

/* ─────────────────────────────── helpers ─────────────────────────────── */
/** Render a row of 1–5 labelled rating buttons */
const RatingRow = ({
  value,
  onChange,
  ratings,
}: {
  value: number | null;
  onChange: (v: number) => void;
  ratings: { value: number; label: string }[];
}) => (
  <div className="flex flex-col gap-3">
    <div className="flex gap-2">
      {ratings.map((r) => (
        <button
          key={r.value}
          type="button"
          onClick={() => onChange(r.value)}
          className={`flex flex-1 flex-col items-center gap-1 rounded-xl border-2 py-3 text-sm font-bold transition-all duration-200 ${
            value === r.value
              ? "border-deepBlue bg-deepBlue text-white shadow-md scale-105"
              : "border-gray-200 text-gray-600 hover:border-gray-400 hover:scale-105"
          }`}
        >
          {r.value}
        </button>
      ))}
    </div>
    <div className="flex justify-between text-xs text-gray-400">
      <span>{ratings[0].label}</span>
      <span>{ratings[ratings.length - 1].label}</span>
    </div>
    {value !== null && (
      <p className="text-center text-sm font-semibold text-deepBlue">
        {ratings.find((r) => r.value === value)?.label}
      </p>
    )}
  </div>
);

/* ─────────────────────────────── component ──────────────────────────── */
const OnboardingSurvey = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [submitted, setSubmitted] = useState(false);

  // ── form fields (aligned with API types) ──
  const [easeOfAccountCreation, setEaseOfAccountCreation] = useState<number | null>(null);
  const [onboardingTimeRating, setOnboardingTimeRating] = useState<number | null>(null);
  const [verificationCodeDelays, setVerificationCodeDelays] = useState<boolean | null>(null);
  const [verificationProcessClarity, setVerificationProcessClarity] = useState<number | null>(null);
  const [frictionPoints, setFrictionPoints] = useState<string[]>([]);  // joined into stuck_points
  const [nextStepsClarity, setNextStepsClarity] = useState<number | null>(null);
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const cardRef = useRef<HTMLDivElement>(null);

  // ── hooks ──
  const { hasSubmitted, checkingStatus } = useOnboardingSurveyStatus();
  const { submitSurvey, submittingSurvey } = useSubmitOnboardingSurvey({
    onSuccess: () => {
      toast.success("Thank you for your feedback! 🎉");
      setSubmitted(true);
    },
    onError: (err) => {
      toast.error(err.message ?? "Something went wrong. Please try again.");
    },
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentStep]);

  // ── friction helpers ──
  const toggleFrictionPoint = (option: string) => {
    setFrictionPoints((cur) => {
      if (option === "None, it was seamless") {
        return cur.includes(option) ? [] : [option];
      }
      return cur.includes(option)
        ? cur.filter((o) => o !== option)
        : [...cur.filter((o) => o !== "None, it was seamless"), option];
    });
  };

  // ── validation per step ──
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 0: return easeOfAccountCreation !== null;
      case 1: return onboardingTimeRating !== null;
      case 2: return verificationCodeDelays !== null;
      case 3: return verificationProcessClarity !== null;
      case 4: return true; // optional
      case 5: return nextStepsClarity !== null;
      case 6: return true; // optional
      default: return true;
    }
  };

  const navigate = (dir: "forward" | "back") => {
    if (animating) return;
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep((s) => (dir === "forward" ? s + 1 : s - 1));
      setAnimating(false);
    }, 260);
  };

  const handleNext = () => {
    if (!isCurrentStepValid()) {
      toast.error("Please answer this question before continuing.");
      return;
    }
    navigate("forward");
  };

  const handleBack = () => navigate("back");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isCurrentStepValid()) {
      toast.error("Please answer all required survey questions.");
      return;
    }

    submitSurvey({
      ease_of_account_creation: easeOfAccountCreation!,
      onboarding_time_rating: onboardingTimeRating!,
      verification_code_delays: verificationCodeDelays!,
      verification_process_clarity: verificationProcessClarity!,
      next_steps_clarity: nextStepsClarity!,
      // Join the selected friction checkboxes into a single string for `stuck_points`
      ...(frictionPoints.length > 0 && {
        stuck_points: frictionPoints.join(", "),
      }),
      ...(additionalFeedback.trim() && {
        additional_feedback: additionalFeedback.trim(),
      }),
      ...(contactPhone.trim() && {
        contact_phone: contactPhone.trim(),
      }),
    });
  };

  const progress = ((currentStep + 1) / TOTAL_STEPS) * 100;

  const slideClass = animating
    ? direction === "forward"
      ? "opacity-0 translate-x-8"
      : "opacity-0 -translate-x-8"
    : "opacity-100 translate-x-0";

  const steps = [
    { number: 1, category: "Overall Experience",         title: "How easy was it to create your Visaro account?",           subtitle: "Rate your experience from 1 (Very Difficult) to 5 (Very Easy)." },
    { number: 2, category: "Speed & Efficiency",          title: "How would you rate the time it took to complete onboarding?", subtitle: "1 = Way too long, 5 = Very fast." },
    { number: 3, category: "Verification Process (OTP)",  title: "Did you experience any delays receiving your verification codes?", subtitle: "This includes codes sent via email or SMS." },
    { number: 4, category: "Identity & KYC",              title: "How clear was the identity & business verification process?", subtitle: "Covers BVN, ID, or Certificate of Incorporation. 1 = Very confusing, 5 = Very clear." },
    { number: 5, category: "Friction Points",             title: "Where did you feel stuck, confused, or frustrated?",       subtitle: "Select all that apply. This is optional." },
    { number: 6, category: "Next Steps Clarity",          title: "After setup, how clearly did you understand what to do next?", subtitle: "E.g., searching for a flight or managing your merchant store. 1 = No idea, 5 = Very clear." },
    { number: 7, category: "Additional Comments",         title: "Any other feedback you'd like to share?",                  subtitle: "All fields on this step are optional." },
  ];

  const current = steps[currentStep];

  /* ── Already-submitted state ── */
  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="flex min-h-[80vh] items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-gray-500">
            <svg className="h-8 w-8 animate-spin text-visaro-orange" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-sm font-medium">Checking survey status…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  /* ── Success / Already-submitted state ── */
  if (submitted || hasSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="flex min-h-[80vh] items-center justify-center px-5 py-10">
          <div className="flex max-w-lg flex-col items-center gap-6 rounded-2xl bg-white p-10 text-center shadow-lg">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-visaro-orange/10 text-4xl">
              {submitted ? "🎉" : "✅"}
            </div>
            <h1 className="text-2xl font-bold text-deepBlue">
              {submitted ? "Thank you for your feedback!" : "Survey Already Submitted"}
            </h1>
            <p className="text-gray-600">
              {submitted
                ? "Your responses help us make Visaro onboarding clearer and smoother for every user. We truly appreciate your time."
                : "You've already completed this survey. We appreciate you taking the time to share your feedback with us!"}
            </p>
            <a
              href="/"
              className="mt-2 inline-block rounded-xl bg-deepBlue px-8 py-3 text-sm font-bold text-white transition hover:opacity-90"
            >
              Back to Home
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="px-5 py-10 md:px-20 lg:px-32">
        <section className="mx-auto flex max-w-2xl flex-col gap-8">

          {/* ── Header ── */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-bold uppercase tracking-widest text-visaro-orange">
              Onboarding Survey
            </span>
            <h1 className="text-3xl font-bold text-deepBlue md:text-4xl">
              Visaro Onboarding Feedback
            </h1>
            <p className="text-gray-500">
              Help us understand what felt smooth, what slowed you down, and how
              we can improve.
            </p>
          </div>

          {/* ── Progress ── */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              {steps.map((step, idx) => (
                <React.Fragment key={step.number}>
                  <div
                    className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                      idx < currentStep
                        ? "bg-visaro-orange text-white"
                        : idx === currentStep
                        ? "bg-deepBlue text-white ring-4 ring-deepBlue/20"
                        : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    {idx < currentStep ? (
                      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      step.number
                    )}
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`h-0.5 flex-1 rounded-full transition-all duration-500 ${idx < currentStep ? "bg-visaro-orange" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                Question <span className="font-bold text-deepBlue">{currentStep + 1}</span> of{" "}
                <span className="font-bold text-deepBlue">{TOTAL_STEPS}</span>
              </span>
              <span className="font-semibold text-deepBlue">{Math.round(progress)}% complete</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-gradient-to-r from-visaro-orange to-deepBlue transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* ── Question Card ── */}
          <form onSubmit={handleSubmit}>
            <div
              ref={cardRef}
              className={`rounded-2xl bg-white p-6 shadow-lg transition-all duration-300 ease-out md:p-10 ${slideClass}`}
            >
              {/* Category pill */}
              <div className="mb-5 inline-block rounded-full bg-visaro-orange/10 px-3 py-1 text-xs font-bold text-visaro-orange">
                {current.category}
              </div>

              <h2 className="mb-2 text-xl font-bold text-deepBlue md:text-2xl">
                {current.title}
              </h2>
              <p className="mb-8 text-sm text-gray-500">{current.subtitle}</p>

              {/* ── Step 1: ease_of_account_creation (emoji picker) ── */}
              {currentStep === 0 && (
                <div className="flex flex-wrap justify-between gap-3">
                  {EASE_RATINGS.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setEaseOfAccountCreation(r.value)}
                      className={`flex flex-1 min-w-[70px] flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all duration-200 ${
                        easeOfAccountCreation === r.value
                          ? "border-deepBlue bg-deepBlue/5 shadow-md scale-105"
                          : "border-gray-200 hover:border-gray-400 hover:scale-105"
                      }`}
                    >
                      <span className="text-3xl">{r.emoji}</span>
                      <span className={`text-xs font-semibold ${easeOfAccountCreation === r.value ? "text-deepBlue" : "text-gray-500"}`}>
                        {r.label}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* ── Step 2: onboarding_time_rating (1-5) ── */}
              {currentStep === 1 && (
                <RatingRow
                  value={onboardingTimeRating}
                  onChange={setOnboardingTimeRating}
                  ratings={TIME_RATINGS}
                />
              )}

              {/* ── Step 3: verification_code_delays (boolean Yes/No) ── */}
              {currentStep === 2 && (
                <div className="flex flex-col gap-3">
                  {[
                    { label: "Yes, I experienced delays", value: true },
                    { label: "No, it was fine", value: false },
                  ].map((option) => (
                    <label
                      key={String(option.value)}
                      className={`flex cursor-pointer items-center gap-4 rounded-xl border-2 p-4 transition-all duration-200 ${
                        verificationCodeDelays === option.value
                          ? "border-deepBlue bg-deepBlue/5"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all ${verificationCodeDelays === option.value ? "border-deepBlue bg-deepBlue" : "border-gray-300"}`}>
                        {verificationCodeDelays === option.value && <span className="h-2 w-2 rounded-full bg-white" />}
                      </span>
                      <input
                        type="radio"
                        name="verificationDelay"
                        checked={verificationCodeDelays === option.value}
                        onChange={() => setVerificationCodeDelays(option.value)}
                        className="sr-only"
                      />
                      <span className={`font-medium ${verificationCodeDelays === option.value ? "text-deepBlue" : "text-gray-700"}`}>
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {/* ── Step 4: verification_process_clarity (1-5) ── */}
              {currentStep === 3 && (
                <RatingRow
                  value={verificationProcessClarity}
                  onChange={setVerificationProcessClarity}
                  ratings={KYC_RATINGS}
                />
              )}

              {/* ── Step 5: stuck_points via friction checkboxes ── */}
              {currentStep === 4 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {FRICTION_OPTIONS.map((option) => {
                    const checked = frictionPoints.includes(option);
                    return (
                      <label
                        key={option}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 p-4 transition-all duration-200 ${
                          checked ? "border-visaro-orange bg-visaro-orange/5" : "border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-all ${checked ? "border-visaro-orange bg-visaro-orange" : "border-gray-300"}`}>
                          {checked && (
                            <svg className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>
                        <input type="checkbox" checked={checked} onChange={() => toggleFrictionPoint(option)} className="sr-only" />
                        <span className={`text-sm font-medium ${checked ? "text-visaro-orange" : "text-gray-700"}`}>
                          {option}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* ── Step 6: next_steps_clarity (1-5) ── */}
              {currentStep === 5 && (
                <RatingRow
                  value={nextStepsClarity}
                  onChange={setNextStepsClarity}
                  ratings={NEXT_STEPS_RATINGS}
                />
              )}

              {/* ── Step 7: additional_feedback + contact_phone ── */}
              {currentStep === 6 && (
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-gray-700" htmlFor="additional_feedback">
                      Additional feedback <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <textarea
                      id="additional_feedback"
                      rows={5}
                      value={additionalFeedback}
                      onChange={(e) => setAdditionalFeedback(e.target.value)}
                      maxLength={2000}
                      placeholder="Tell us more about your onboarding experience…"
                      className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-gray-700 transition focus:border-deepBlue focus:outline-none"
                    />
                    <p className="text-right text-xs text-gray-400">{additionalFeedback.length}/2000</p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-gray-700" htmlFor="contact_phone">
                      Phone number <span className="font-normal text-gray-400">(optional — if you'd like us to follow up)</span>
                    </label>
                    <input
                      id="contact_phone"
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      placeholder="+2348012345678"
                      className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-gray-700 transition focus:border-deepBlue focus:outline-none"
                    />
                  </div>
                </div>
              )}

              {/* ── Navigation ── */}
              <div className="mt-8 flex items-center justify-between border-t border-gray-100 pt-6">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="flex items-center gap-2 rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-600 transition hover:border-gray-400 hover:text-deepBlue disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Back
                </button>

                {currentStep < TOTAL_STEPS - 1 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="flex items-center gap-2 rounded-xl bg-deepBlue px-6 py-3 text-sm font-bold text-white shadow transition hover:opacity-90 active:scale-95"
                  >
                    Continue
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={submittingSurvey}
                    className="flex items-center gap-2 rounded-xl bg-visaro-orange px-6 py-3 text-sm font-bold text-white shadow transition hover:opacity-90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submittingSurvey ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Submitting…
                      </>
                    ) : (
                      <>
                        Submit Feedback
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>

          <p className="text-center text-xs text-gray-400">
            This survey is completely optional.{" "}
            <a href="/" className="text-visaro-orange underline underline-offset-2 hover:opacity-80">
              Skip for now
            </a>
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OnboardingSurvey;
